// CRM feature-flag registry.
//
// Phase 1 keeps this simple: env-backed defaults + a workspace override slot
// that always resolves to `undefined` (no WorkspaceSettings table yet). When
// Week 5 ships WorkspaceSettings, the host app passes real overrides via
// `useFlagsCtx()` below — nothing else in the app needs to change.
//
// Known flags (aligned with @govard/feature-flags/DEFAULT_FLAGS plus our own):
//   - 'pro-mode'       → Pro sidebar section visibility
//   - 'ai-chat'        → ChatPanel slot visibility
//   - 'ai-cockpit'     → Docked AI cockpit panel in AppLayout (default off)
//   - 'copilotkit-lab' → CopilotKit GenUI lab page (/copilot)

import {
  createFlagsRegistry,
  DEFAULT_FLAGS,
  type FlagId,
  type FlagResolutionContext,
  type FlagsRegistry,
} from '@govard/feature-flags';

import { useScopeStore } from '../shared/scope/useCurrentScope.js';

function readBoolEnv(raw: string | undefined, fallback: boolean): boolean {
  if (raw === undefined) return fallback;
  const normalised = raw.trim().toLowerCase();
  if (normalised === 'true' || normalised === '1') return true;
  if (normalised === 'false' || normalised === '0') return false;
  return fallback;
}

/**
 * Registry wired with CRM-local defaults, merged over package defaults so
 * canonical flags (`ai-chat`, `enrich-contact-experimental`) stay available.
 */
export const flagsRegistry: FlagsRegistry = createFlagsRegistry({
  flags: {
    ...DEFAULT_FLAGS,
    'pro-mode': {
      defaultEnabled: readBoolEnv(import.meta.env.VITE_FEATURE_PRO_MODE, false),
      description: 'Enable Pro-mode advanced sidebar section (CRM)',
    },
    'ai-chat': {
      defaultEnabled: readBoolEnv(import.meta.env.VITE_FEATURE_AI_CHAT, true),
      description: 'Show AI chat panel in AppLayout (CRM)',
    },
    'ai-cockpit': {
      // Default off on purpose: the cockpit docks a live chat next to every
      // supported route and is rolled out per workspace, never by surprise.
      defaultEnabled: readBoolEnv(import.meta.env.VITE_FEATURE_AI_COCKPIT, false),
      description: 'Show the docked AI cockpit panel in AppLayout (CRM)',
    },
    'copilotkit-lab': {
      defaultEnabled: readBoolEnv(import.meta.env.VITE_FEATURE_COPILOTKIT_LAB, false),
      description: 'Show CopilotKit GenUI lab page at /copilot (CRM)',
    },
    'run-center': {
      defaultEnabled: readBoolEnv(import.meta.env.VITE_FEATURE_RUN_CENTER, false),
      description: 'Show Run-Center page and nav item (CRM)',
    },
  },
});

/**
 * Builds a resolution context from the current Zustand store. Falls back to a
 * sentinel 'anonymous' workspaceId/userId when no session is mounted — flags
 * evaluated in that state will just return the default, which is correct.
 *
 * TODO [WEEK-5]: read `workspaceFlags` from WorkspaceSettings once it lands.
 */
interface BuildFlagsCtxInput {
  scope?: { workspaceId: string; role?: string } | null;
  user?: { id: string } | null;
  workspaceSettings?: { featureFlags: Record<string, boolean> } | null;
}

export function buildFlagsCtx(input?: BuildFlagsCtxInput): FlagResolutionContext {
  const state = input ?? useScopeStore.getState();
  const workspaceId = state.scope?.workspaceId ?? 'anonymous';
  const userId = state.user?.id ?? 'anonymous';
  const role = state.scope?.role;
  const ctx: FlagResolutionContext = {
    workspaceId,
    userId,
    workspaceFlags: state.workspaceSettings?.featureFlags ?? {},
  };
  if (role !== undefined) {
    ctx.role = role;
  }
  return ctx;
}

/** Canonical flag IDs consumed by the shell. */
export const CRM_FLAG_IDS = {
  PRO_MODE: 'pro-mode' satisfies FlagId,
  AI_CHAT: 'ai-chat' satisfies FlagId,
  AI_COCKPIT: 'ai-cockpit' satisfies FlagId,
  COPILOTKIT_LAB: 'copilotkit-lab' satisfies FlagId,
  RUN_CENTER: 'run-center' satisfies FlagId,
} as const;
